<script setup lang="ts">
import { ref, onMounted, computed, watch, provide, onUnmounted } from "vue";
import { RouterView, useRouter } from "vue-router";
import Sidebar from "@/components/Sidebar.vue";
import CommandK from "@/components/CommandK.vue";
import FundNavItem from "@/components/FundNavItem.vue";
import type { PriceDataObject, StockDataObject, UserData, HoldingDataObject, Funds, PricesAfterHoursObject } from "@/interfaces/interfaces";
import type { Ref } from "vue";
import { APIURL } from "@/defaults";
import LoginView from "@/views/LoginView.vue";
import MobileView from "@/views/MobileView.vue";
import type { SignalsData } from "@/interfaces/interfaces.js"
import TickerBar from "./components/TickerBar.vue";


const loggedin = ref(false);
const userData: Ref<UserData | null> = ref(null);
provide('userData', userData);

const privacyMode: Ref<boolean> = ref(false);

const stocks: Ref<StockDataObject | null> = ref(null);
const prices: Ref<PriceDataObject | null> = ref(null);
const prices_afterhours: Ref<PricesAfterHoursObject | null> = ref(null);
const holdings: Ref<HoldingDataObject | null> = ref(null);
const signals: Ref<SignalsData | null> = ref(null);
provide('stocks', stocks)
provide('prices', prices)
provide('prices_afterhours', prices_afterhours)
provide('holdings', holdings)
provide('signals', signals)

const wsPubSub = {
  subscribers: [] as Array<(msg: any) => void>,
  subscribe(callback: (msg: any) => void) {
    this.subscribers.push(callback);
  },
  unsubscribe(callback: (msg: any) => void) {
    this.subscribers = this.subscribers.filter((cb) => cb !== callback);
  },
  publish(message: any) {
    this.subscribers.forEach((cb) => cb(message));
  },
};
provide('wsPubSub', wsPubSub);

const router = useRouter();
const sidebarVisible = ref(true);

const isMobile = ref(false);

const funds: Funds = {
  "205410": {
    id: "205410",
    name: "Disruptive Tech",
    fullname: "Disruptive Technologies",
  },
  "205310": {
    id: "205310",
    name: "Disruptive Tech Inst",
    fullname: "Disruptive Technologies Institutional",
  },
  "206910": {
    id: "206910",
    name: "Small & Mid Cap Tech",
    fullname: "Small & Mid Cap Technologies",
  },
  "207610": {
    id: "207610",
    name: "Clean Tech",
    fullname: "Clean Technologies",
  },
};
provide('funds', funds)

const fundsSorted = Object.values(funds).sort((a,b) => {
  const ints = [Number.parseInt(a.id), Number.parseInt(b.id)]
  const smallId = Math.min(...ints);
  const largeId = Math.max(...ints);

  let flipFactor = 1;
  if(smallId === 205310 && largeId === 205410) {
    flipFactor = -1;
  }

  return (ints[0] - ints[1]) * flipFactor;
})

const currentFund: Ref<string> = ref(
  (() => {
    const storedFund = localStorage.getItem("currentFund");
    return storedFund && funds[storedFund] ? storedFund : "205410";
  })(),
);

const setCurrentFund = (fundId: string) => {
  currentFund.value = fundId;
};

const updatePrices = () => {
  fetch(`${APIURL}/prices`, { credentials: "include" })
    .then((r) => r.json())
    .then((r) => {
      prices.value = r;
    })
    .catch(err => {
      console.error("Prices not loaded", err);
    });

  fetch(`${APIURL}/prices-afterhours`, { credentials: "include" })
    .then((r) => r.json())
    .then((r) => {
      prices_afterhours.value = r;
    })
    .catch(err => {
      console.error("Afterhours Prices not loaded", err);
    });

  fetch(`${APIURL}/stock-signals`, { credentials: "include" })
    .then((r) => r.json())
    .then((r) => {
      signals.value = r;
    });
};

// Track holdings request to prevent race conditions
const holdingsRequestInfo = ref({ fundId: '', requestId: 0 });

const updateHoldings = () => {
  if (!loggedin.value || !currentFund.value) return;

  // Set holdings to null immediately to prevent using stale data
  holdings.value = null;

  // Update request tracking
  const requestId = holdingsRequestInfo.value.requestId + 1;
  const requestFundId = currentFund.value;
  holdingsRequestInfo.value = { fundId: requestFundId, requestId };

  // Capture the fund ID at request time for validation
  // console.log(`Requesting holdings for fund: ${requestFundId}, request ID: ${requestId}`);

  fetch(`${APIURL}/fund/${requestFundId}/holdings`, { credentials: "include" })
    .then((r) => {
      // Verify this is still the latest request for the right fund
      if (holdingsRequestInfo.value.requestId !== requestId ||
          holdingsRequestInfo.value.fundId !== requestFundId) {
        // console.log(`Holdings request ${requestId} was superseded or fund changed`);
        return Promise.reject(new Error("Superseded by newer holdings request or fund changed"));
      }

      if (r.status !== 200) {
        throw new Error("Failed to fetch holdings");
      }
      return r.json();
    })
    .then((r) => {
      // Double check before updating that we're still on the same request and fund
      if (holdingsRequestInfo.value.requestId !== requestId ||
          holdingsRequestInfo.value.fundId !== requestFundId ||
          currentFund.value !== requestFundId) {
        // console.log(`Holdings response for ${requestFundId} rejected - no longer current fund`);
        return;
      }

      // console.log(`Holdings loaded for fund: ${requestFundId}, request ID: ${requestId}`);
      holdings.value = r;
    })
    .catch((err) => {
      // Only update error state if this is still the current request
      if (holdingsRequestInfo.value.requestId === requestId &&
          holdingsRequestInfo.value.fundId === requestFundId &&
          err.message !== "Superseded by newer holdings request or fund changed") {
        console.error("Holdings not loaded", err);
      }
    });
};

let priceUpdateInterval: NodeJS.Timeout | null = null;

const updatePeriodically = () => {
  updatePrices();
};

const updateInitially = () => {
  if (loggedin.value) {
    // console.log("Updating initial data");
    fetch(`${APIURL}/stocks`, { credentials: "include" })
      .then((r) => {
        if (r.status !== 200) {
          throw new Error("Failed to fetch stocks");
        }
        return r.json();
      })
      .then((r) => {
        const stocks_obj: StockDataObject = {};
        for (const s of r) {
          const isin = s.isin;
          stocks_obj[isin] = s;
        }
        stocks.value = stocks_obj;
        // console.log("Stocks loaded:", Object.keys(stocks_obj).length);
      })
      .catch((err) => {
        console.error("Stocks not loaded", err);
      });

    // Also ensure prices are loaded
    updatePrices();

    // Load holdings for current fund
    updateHoldings();
  }
}

const checkIfMobile = () => {
  const mobileUserAgent = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const smallScreen = window.innerWidth < 1024;

  isMobile.value = smallScreen;
};

const handleResize = () => {
  checkIfMobile();
};

const addShortcuts = () => {
  // privacy mode
  document.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }
    if (e.target && e.target instanceof HTMLElement && e.target.isContentEditable) {
      return;
    }

    if (e.code === "KeyP" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      privacyMode.value = !privacyMode.value;
    }

    // set fund by number keys 1 to 5 (including numpad)
    const match = e.code.match(/(?:Digit|Numpad)([1-5])/);
    if ((e.code.startsWith('Digit') || e.code.startsWith('Numpad')) && match) {
      const index = Number(match[1]) - 1;
      if (index >= 0 && index < fundsSorted.length) {
        setCurrentFund(fundsSorted[index].id);
      }
    }

    // navigation shortcuts
    if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
      switch (e.code) {
        case "KeyA":
          router.push("/holdings");
          break;
        case "KeyH":
          router.push("/");
          break;
        case "KeyS":
          router.push("/signals");
          break;
        case "KeyF":
          router.push("/feed");
          break;
        case "KeyI":
          router.push("/cashflow");
          break;
        case "KeyR":
          router.push("/risk");
          break;
        case "KeyC":
          router.push("/returncontributions");
          break;
        case "KeyM":
          router.push("/macro");
          break;
        case "KeyP":
          router.push("/comparison");
          break;
        case "KeyO":
          if (userData.value?.permissions.orders) {
            router.push("/orders");
            break;
          }

      }
    }

    if (e.code === "KeyB" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      toggleSidebar();
    }
  });
  if (localStorage.getItem("privacy") === "true") {
    privacyMode.value = true;
  }
};

const privacyModeToggle = () => {
  localStorage.setItem("privacy", String(privacyMode.value));

  if (privacyMode.value) {
    const icons = Array.from(document.head.querySelectorAll("link[rel~='icon']"));
    for (const icon of icons) {
      icon.setAttribute(
        "href",
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="4" fill="white" /></svg>',
      );
    }
    const title = document.head.querySelector("title");
    if (title) {
      title.innerHTML = "Dashboard";
    }
  }
};

const toggleSidebar = () => {
  sidebarVisible.value = !sidebarVisible.value;
};

let ws: WebSocket | null;
let wsReconnectTimer: ReturnType<typeof setTimeout>;

const connectWebsocket = () => {
  if (!loggedin.value) return;
  if (!userData.value?.user_id) return;

  const id = encodeURIComponent(userData.value.user_id);

  const ws_url = APIURL.replace('https://', 'wss://').replace('http://', 'ws://')
  ws = new WebSocket(`${ws_url}/ws/${id}`);
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      wsPubSub.publish(data);
      console.log(data);
      if(data.event === "prices_updated") {
        updatePrices();
      }
      if(data.event === "holdings_updated") {
        // Only update if it's for the current fund
        const fundId = currentFund.value;
        if (data.fund_id === fundId) {
          console.log(`Received holdings_updated event for fund ${data.fund_id}, current fund is ${fundId}`);
          updateHoldings();
        } else {
          console.log(`Ignored holdings_updated event for non-current fund ${data.fund_id}`);
        }
      }
    } catch (error) {
      console.log(event.data);
    }
  };
  ws.onopen = () => {
    console.log('WebSocket connection established.');
  };
  ws.onclose = (event) => {
    // Don't reconnect if it was a policy violation (code 1008)
    if (event.code === 1008) {
      console.log('WebSocket connection closed due to policy violation. Not attempting to reconnect.');
      return;
    }

    console.log('WebSocket connection closed. Attempting to reconnect...');
    wsReconnectTimer = setTimeout(connectWebsocket, 10000); // Attempt to reconnect every 10 seconds
  };
}

const disconnectWebsocket = () => {
  if (ws) {
    ws.close();
    ws = null;
    clearTimeout(wsReconnectTimer);
  }
}

// Watch for login state changes to manage WebSocket connection
watch(() => loggedin.value, (newValue) => {
  if (newValue) {
    connectWebsocket();
  } else {
    disconnectWebsocket();
  }
});

onMounted(() => {
  priceUpdateInterval = setInterval(updatePeriodically, 60e3 * 15);
  updateInitially();

  checkIfMobile();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);

  if (priceUpdateInterval !== null) {
    clearInterval(priceUpdateInterval);
    priceUpdateInterval = null;
  }

  disconnectWebsocket();
});

onMounted(addShortcuts);

watch(() => privacyMode.value, privacyModeToggle);
watch(currentFund, (newValue) => {
  localStorage.setItem("currentFund", newValue);
  updateHoldings(); // Update holdings when fund changes
});

const handleLoginComplete = (data: UserData) => {
  loggedin.value = true;
  userData.value = data;
  updateInitially();
};
</script>

<template>
  <div v-if="!loggedin">
    <LoginView @login-complete="handleLoginComplete" />
  </div>
  <div v-else-if="loggedin && userData">
    <!-- Mobile view is completely separate from desktop view -->
    <div v-if="isMobile" class="mobile-wrapper">
      <MobileView
        :fund_id="currentFund"
        @update:fund_id="setCurrentFund"
      />
    </div>

    <template v-else-if="stocks && prices">
      <TickerBar />
      <div class="app-container" :class="{ 'privacy': privacyMode }">
        <header class="app-header">
          <div class="wrapper">
            <div class="logo-container">
              <img alt="TEQ Cpaital Logo" class="logo" src="@/assets/teq-logo-horizontal.svg" width="4620" height="432" />
            </div>

            <div class="fund-select-nav">
              <FundNavItem @click.prevent="setCurrentFund(fund.id)" :fund_id="fund.id" :fund_name="fund.name"
                :active="fund.id === currentFund" v-for="fund in fundsSorted" :key="fund.id" />
            </div>
          </div>
        </header>

        <div class="app-wrapper">
          <Sidebar v-show="sidebarVisible" :fund_id="currentFund" />
          <main class="app-body" :class="{ 'full-width': !sidebarVisible }">
            <RouterView :fund_id="currentFund" v-if="prices && stocks" />
          </main>
          <CommandK />
        </div>
      </div>
    </template>

    <div v-else class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading data...</p>
    </div>
  </div>
</template>

<style>
@import "@/assets/base.css";

/* Mobile wrapper - ensures no scroll interference */
.mobile-wrapper {
  /* Remove any properties that might interfere with natural scrolling */
  position: static;
  overflow: visible;
  height: auto;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(226, 159, 118, 0.3);
  border-radius: 50%;
  border-top-color: rgb(226, 159, 118);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Desktop-specific styles */
@media (min-width: 1024px) {
  .app-container {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-row-gap: 20px;
    height: calc(100vh - 40px);
    max-width: 1580px;
  }

  .app-wrapper {
    display: grid;
    grid-template-columns: 300px 1fr;
    position: relative;
    overflow: hidden;
  }

  .app-body {
    padding: 0 40px 40px 40px;
    overflow: scroll;
    max-height: calc(100%);
    box-sizing: border-box;
    position: relative;
  }

  .app-body.full-width {
    grid-column: 1 / -1;
  }
}

/* Mobile-specific styles */
@media (max-width: 1023px) {
  .app-container {
    display: block;
    height: auto;
  }

  .app-wrapper {
    display: block;
    overflow: visible;
  }

  .app-body {
    padding: 0 20px 20px 20px;
    overflow: visible;
    height: auto;
    position: static;
  }
}

.app-header {
  line-height: 1.5;
}

.app-header .wrapper {
  padding: 0 20px;
  display: flex;
  justify-content: flex-start;
}

.app-header .logo-container {
  flex: 0 0 260px;
  margin-right: 60px;
}

.app-header .logo {
  display: block;
  margin: 0;
  padding: 20px 0;
}

.privacy .app-header .logo {
  visibility: hidden;
}

.app-header .wrapper {
  padding-right: 40px;
}

a {
  text-decoration: none;
  color: #E29F76;
}

@media (hover: hover) {
  a:hover {
    color: #F2955C;
  }
}

.fund-select-nav {
  display: flex;
  gap: 20px;
  justify-content: space-between;
  width: 100%;
}

.prices-updated {
  text-transform: uppercase;
  font-size: 10px;
  opacity: 0.4;
  letter-spacing: 0.1em;
}
</style>
